import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { BLOG_POSTS, BLOG_CATEGORIES, type BlogPost } from "@/content/blog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, BookOpen } from "lucide-react";

const Blog = () => {
  const [filter, setFilter] = useState<BlogPost["category"] | "all">("all");
  const posts = filter === "all" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog Click&Gard",
    description:
      "Conseils, guides et tarifs pour trouver une baby-sitter, une nounou ou une garde d'enfants en France.",
    url: "https://clickandgard.fr/blog",
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.description,
      datePublished: p.publishedAt,
      url: `https://clickandgard.fr/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Blog — guides baby-sitting, nounou et démarches"
        description="Tous nos guides pour trouver une baby-sitter ou une nounou : tarifs par ville, démarches Pajemploi, conseils. Mis à jour 2026."
        path="/blog"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="container mx-auto px-4 py-10 max-w-6xl">
        <header className="mb-8 text-center space-y-3">
          <Badge variant="secondary" className="gap-1">
            <BookOpen className="w-3.5 h-3.5" /> Blog Click&Gard
          </Badge>
          <h1 className="text-4xl md:text-5xl font-display font-bold">
            Guides baby-sitting, nounou et garde d'enfants
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Tarifs par ville, démarches Pajemploi, conseils pratiques. Tout pour trouver la garde
            idéale en France en 2026.
          </p>
        </header>

        <nav className="flex flex-wrap gap-2 justify-center mb-8" aria-label="Catégories">
          <Button
            size="sm"
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
          >
            Tous ({BLOG_POSTS.length})
          </Button>
          {BLOG_CATEGORIES.map((c) => {
            const count = BLOG_POSTS.filter((p) => p.category === c.id).length;
            return (
              <Button
                key={c.id}
                size="sm"
                variant={filter === c.id ? "default" : "outline"}
                onClick={() => setFilter(c.id)}
              >
                {c.label} ({count})
              </Button>
            );
          })}
        </nav>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <Badge variant="outline" className="text-[10px]">
                      {BLOG_CATEGORIES.find((c) => c.id === post.category)?.label}
                    </Badge>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.readingMinutes} min
                    </span>
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary transition-colors">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-3">{post.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.publishedAt).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Blog;
